import React, { useState, useCallback, useEffect, useRef } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Bell, Snowflake, ArrowRightLeft, ChevronDown } from 'lucide-react';
import EmailGate from '../components/EmailGate';
import { useIndustry } from '../context/IndustryContext';

const croNav = [
  { to: '/dashboard/risk-radar', label: 'Risk Radar', end: true },
  { to: '/dashboard/opportunities', label: 'Opportunities' },
  { to: '/dashboard/teams', label: 'Teams' },
  { to: '/dashboard/settings', label: 'Settings' },
];

const repNav = [
  { to: '/dashboard/todays-focus', label: "Today's Focus", end: true },
  { to: '/dashboard/opportunities', label: 'Opportunities' },
];

const croPageTitles = {
  '/dashboard/risk-radar': 'Risk Radar',
  '/dashboard/opportunities': 'Opportunities',
  '/dashboard/teams': 'Teams',
  '/dashboard/settings': 'Settings',
};

const repPageTitles = {
  '/dashboard/todays-focus': "Today's Focus",
  '/dashboard/opportunities': 'Opportunities',
};

const profiles = {
  cro: { name: 'Sarah Chen', role: 'Admin' },
  rep: { name: 'Marcus Johnson', role: 'CSM' },
};

export default function Dashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [emailVerified, setEmailVerified] = useState(false);
  const { selectedIndustry, setSelectedIndustry, industries } = useIndustry();

  // Auto-detect role from current path
  const isRepPath = location.pathname === '/dashboard/todays-focus';
  const [activeRole, setActiveRole] = useState('rep');

  const handleEmailSubmit = useCallback((email) => {
    setEmailVerified(true);
  }, []);

  // Track page visits to Slack
  const lastNotified = useRef('');
  useEffect(() => {
    if (!emailVerified) return;
    const key = `${location.pathname}|${selectedIndustry}`;
    if (key === lastNotified.current) return;
    lastNotified.current = key;

    const titles = activeRole === 'cro' ? croPageTitles : repPageTitles;
    const pageName = titles[location.pathname] || location.pathname.split('/').pop();
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/events/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_type: 'page_visit',
        page: pageName,
        role: activeRole === 'cro' ? 'CRO' : 'Sales Rep',
        industry: selectedIndustry,
      }),
    }).catch(() => {});
  }, [location.pathname, selectedIndustry, emailVerified, activeRole]);

  const navItems = activeRole === 'cro' ? croNav : repNav;
  const pageTitles = activeRole === 'cro' ? croPageTitles : repPageTitles;
  const profile = profiles[activeRole];
  const pageTitle = pageTitles[location.pathname] || (location.pathname.startsWith('/dashboard/opportunities/') ? 'Opportunities' : (activeRole === 'cro' ? 'Risk Radar' : "Today's Focus"));

  const handleRoleSwitch = () => {
    const newRole = activeRole === 'cro' ? 'rep' : 'cro';
    setActiveRole(newRole);
    if (newRole === 'cro') {
      navigate('/dashboard/risk-radar');
    } else {
      navigate('/dashboard/todays-focus');
    }
  };

  return (
    <div className="flex h-screen bg-gray-50" data-testid="dashboard-layout">
      {/* Email Gate Overlay */}
      {!emailVerified && <EmailGate onSubmit={handleEmailSubmit} />}

      {/* Sidebar */}
      <aside className="w-[180px] min-w-[180px] bg-white border-r border-gray-200 flex flex-col" data-testid="dashboard-sidebar">
        {/* Logo */}
        <div className="px-5 pt-6 pb-8">
          <div className="flex items-center gap-2">
            <img
              src="/assets/gt687tca.png"
              alt="Forsyt"
              className="h-8 w-auto"
            />
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 space-y-1" data-testid="dashboard-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center px-3 py-2.5 text-[14px] font-medium rounded-md transition-colors ${
                  isActive
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/[' ]/g, '-')}`}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Industry Selector + Role Switch + User Profile */}
        <div className="border-t border-gray-100">
          {/* Industry Selector */}
          <div className="px-3 py-3 border-b border-gray-100" data-testid="industry-selector-section">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-1 mb-1.5">Industry</p>
            <div className="relative">
              <select
                value={selectedIndustry}
                onChange={(e) => setSelectedIndustry(e.target.value)}
                className="w-full text-[11px] text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-2 py-2 pr-7 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer appearance-none"
                data-testid="industry-selector"
              >
                {industries.map(ind => (
                  <option key={ind} value={ind}>{ind}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Role Switcher */}
          <button
            onClick={handleRoleSwitch}
            className="w-full flex items-center gap-2.5 px-4 py-3 text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors"
            data-testid="role-switch-btn"
          >
            <ArrowRightLeft className="w-3.5 h-3.5" />
            Switch to {activeRole === 'cro' ? 'Sales Rep' : 'CRO'} View
          </button>

          {/* User Profile */}
          <div className="px-4 py-3" data-testid="user-profile">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                <Snowflake className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{profile.name}</p>
                <p className="text-xs text-gray-500">{profile.role}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 px-8 py-5 flex items-center justify-between flex-shrink-0" data-testid="dashboard-header">
          <h1 className="text-2xl font-bold text-gray-900">{pageTitle}</h1>
          <div className="flex items-center gap-4">
            <button className="text-gray-400 hover:text-gray-600 transition-colors" data-testid="notification-bell">
              <Bell className="w-5 h-5" />
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6" data-testid="dashboard-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
